import {
    AlertTriangle,
    Archive,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    ChevronUp,
    Copy,
    FileText,
    Info,
    LoaderCircle,
    MoreVertical,
    Pencil,
    Plus,
    RefreshCw,
    Sparkles,
    Tag,
    Trash2,
    Users,
    X,
} from 'lucide-react'
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './SalaryStructureDetailScreen.module.css'

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const SUGGESTED_COMPONENTS = [
    { code: 'BASIC',            label: 'Basic Pay',              required: true  },
    { code: 'HRA',              label: 'House Rent Allowance',   required: false },
    { code: 'TRAVEL_ALLOWANCE', label: 'Travel Allowance',       required: false },
    { code: 'LWP_DEDUCTION',    label: 'LWP Deduction',          required: true  },
    { code: 'PF_EMPLOYEE',      label: 'Provident Fund',         required: false },
]

const formatDate = (iso) => {
    if (!iso) return '—'
    const d = new Date(iso)
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}

const SalaryStructureDetailScreen = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { templateIdentifier } = useParams()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)
    const storeIdentifier = activeStore?.storeIdentifier || ''

    // Allow templateName from navigation state as a pre-load label
    const routeTemplateName = location.state?.templateName || ''

    const [detailData, setDetailData] = useState(null)
    const [isLoading, setIsLoading] = useState(true)
    const [isRefreshing, setIsRefreshing] = useState(false)
    const [activeTab, setActiveTab] = useState('COMPONENTS')
    const [removedExpanded, setRemovedExpanded] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)
    const [isRemovingId, setIsRemovingId] = useState(null)
    const [confirmSheet, setConfirmSheet] = useState(null)
    const [isRetiring, setIsRetiring] = useState(false)
    const [showRetireSheet, setShowRetireSheet] = useState(false)
    const [blockedStaffData, setBlockedStaffData] = useState(null)
    const menuRef = useRef(null)

    const fetchTemplateDetail = useCallback(
        async ({ silent = false } = {}) => {
            if (!authCtx?.token || !storeIdentifier || !templateIdentifier) {
                if (!silent) setIsLoading(false)
                return
            }
            if (!silent) setIsLoading(true)
            else setIsRefreshing(true)

            try {
                const response = await fetch(BASE_URL_API + 'getSalaryStructureTemplateDetail', {
                    method: 'POST',
                    body: JSON.stringify({
                        token: authCtx.token,
                        storeIdentifier,
                        templateIdentifier,
                    }),
                })

                if (!response.ok) {
                    toast.error('Failed to load template detail.')
                    return
                }

                const respo = await response.json()

                if (respo?.status === 'success') {
                    setDetailData(respo.response)
                    return
                }

                if (respo?.message === 'tokenExpired') {
                    authCtx.logout()
                    return
                }

                toast.error(respo?.message || 'Failed to load template.')
                navigate(-1)
            } catch (error) {
                console.error('getSalaryStructureTemplateDetail error:', error)
                toast.error('Network error. Please check your connection.')
            } finally {
                setIsLoading(false)
                setIsRefreshing(false)
            }
        },
        [authCtx, storeIdentifier, templateIdentifier, navigate]
    )

    useEffect(() => {
        fetchTemplateDetail()
    }, [fetchTemplateDetail])

    // Refresh on back-nav from add/revise/remove component screens
    useEffect(() => {
        if (!storeIdentifier) return
        fetchTemplateDetail({ silent: true })
    }, [location.key]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const onFocus = () => { if (storeIdentifier) fetchTemplateDetail({ silent: true }) }
        const onVisibility = () => { if (document.visibilityState === 'visible') onFocus() }
        window.addEventListener('focus', onFocus)
        document.addEventListener('visibilitychange', onVisibility)
        return () => {
            window.removeEventListener('focus', onFocus)
            document.removeEventListener('visibilitychange', onVisibility)
        }
    }, [fetchTemplateDetail, storeIdentifier])

    // Close action menu on outside click
    useEffect(() => {
        const handler = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false)
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [])

    const template = detailData?.template
    const components = detailData?.components
    const summary = detailData?.summary
    const assignedStaff = Array.isArray(detailData?.assignedStaff) ? detailData.assignedStaff : []
    const activeComponents = Array.isArray(components?.active) ? components.active : []
    const inactiveComponents = Array.isArray(components?.inactive) ? components.inactive : []
    const earnings = activeComponents.filter((c) => c.componentCategory === 'EARNING').sort((a, b) => a.sortOrder - b.sortOrder)
    const deductions = activeComponents.filter((c) => c.componentCategory === 'DEDUCTION').sort((a, b) => a.sortOrder - b.sortOrder)
    const isArchived = template?.isActive === 0
    const canRetire = (detailData?.totalStaffAssigned ?? 0) === 0
    const displayName = template?.templateName || routeTemplateName || 'Salary Template'

    const goToAddComponent = (prefilledCode = '') => {
        setMenuOpen(false)
        navigate(`/payroll/salary-structure/${templateIdentifier}/add-component`, {
            state: { templateIdentifier, templateName: displayName, prefilledCode },
        })
    }

    const goToEditComponent = (component) => {
        navigate(`/payroll/salary-structure/${templateIdentifier}/edit-component/${component.componentIdentifier}`, {
            state: { templateIdentifier, templateName: displayName, component },
        })
    }

    const handleRemovePress = (component) => {
        if (component.componentCode === 'BASIC') {
            const percentDependents = activeComponents.filter(
                (currentComp) =>
                    currentComp.valueType === 'PERCENT_OF_BASIC' &&
                    currentComp.componentIdentifier !== component.componentIdentifier
            )

            if (percentDependents.length > 0) {
                const depNames = percentDependents.map((dep) => `"${dep.componentName}"`).join(', ')
                toast.error(`Remove ${depNames} first before removing BASIC.`)
                return
            }
        }

        setConfirmSheet({
            componentIdentifier: component.componentIdentifier,
            componentName: component.componentName,
            componentCode: component.componentCode,
            isStatutory: component.isStatutory === 1,
        })
    }

    const removeComponent = async (componentIdentifier, componentName) => {
        if (!authCtx?.token || !storeIdentifier || !templateIdentifier) return

        try {
            setIsRemovingId(componentIdentifier)
            setConfirmSheet(null)

            const response = await fetch(BASE_URL_API + 'removeSalaryStructureComponent', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier,
                    templateIdentifier,
                    componentIdentifier,
                }),
            })

            if (!response.ok) {
                toast.error('Something went wrong. Please try again.')
                return
            }

            const respo = await response.json()

            if (respo?.status === 'success') {
                if (Array.isArray(respo?.response?.warnings) && respo.response.warnings.length > 0) {
                    respo.response.warnings.forEach((warning) => {
                        toast.warning(warning.message)
                    })
                }
                toast.success(`"${componentName}" removed from template.`)
                fetchTemplateDetail({ silent: true })
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            switch (respo?.errorCode) {
                case 'COMP_002': {
                    const depNames = respo?.response?.dependentComponents
                        ?.map((dep) => `"${dep.componentName}"`)
                        .join(', ') || ''
                    toast.error(`Cannot remove BASIC. First remove or convert: ${depNames}`)
                    break
                }
                case 'VAL_007':
                    toast.error('Component no longer exists. It may have already been removed.')
                    fetchTemplateDetail({ silent: true })
                    break
                case 'VAL_008':
                    toast.error(respo?.message || 'Cannot remove this component.')
                    break
                case 'COMP_004':
                    toast.error(respo?.message || 'Salary structure template not found.')
                    navigate(-1)
                    break
                default:
                    toast.error(respo?.message || 'Failed to remove component.')
                    break
            }
        } catch (error) {
            console.error('removeSalaryStructureComponent error:', error)
            toast.error('Network error. Please check your connection.')
        } finally {
            setIsRemovingId(null)
        }
    }

    const handleRetirePress = () => {
        setMenuOpen(false)

        if (!canRetire) {
            toast.info(`${detailData?.totalStaffAssigned || 0} staff assigned — reassign them first.`)
            return
        }

        setShowRetireSheet(true)
    }

    const retireTemplate = async () => {
        if (!authCtx?.token || !storeIdentifier || !templateIdentifier) return

        try {
            setIsRetiring(true)
            setShowRetireSheet(false)

            const response = await fetch(BASE_URL_API + 'retireSalaryStructureTemplate', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier,
                    templateIdentifier,
                }),
            })

            if (!response.ok) {
                toast.error('Something went wrong. Please try again.')
                return
            }

            const respo = await response.json()

            if (respo?.status === 'success') {
                if (Array.isArray(respo?.response?.warnings) && respo.response.warnings.length > 0) {
                    respo.response.warnings.forEach((warning) => toast.warning(warning.message))
                }

                toast.success(`"${respo?.response?.templateName || displayName}" archived successfully.`)
                navigate(-1)
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            switch (respo?.errorCode) {
                case 'COMP_003':
                    setBlockedStaffData(respo?.response || null)
                    break
                case 'VAL_008':
                    toast.error(respo?.message || 'Template already archived.')
                    navigate(-1)
                    break
                case 'COMP_004':
                    toast.error(respo?.message || 'Salary structure template not found.')
                    navigate(-1)
                    break
                default:
                    toast.error(respo?.message || 'Failed to archive template.')
                    break
            }
        } catch (error) {
            console.error('retireSalaryStructureTemplate error:', error)
            toast.error('Network error. Please check your connection.')
        } finally {
            setIsRetiring(false)
        }
    }

    // ── Render helpers ─────────────────────────────────────────────
    const renderComponentCard = (component, showActions = true) => {
        const isStatutory = component.isStatutory === 1
        const isEarning = component.componentCategory === 'EARNING'
        const isInactive = component.isActive === 0

        return (
            <article
                key={component.componentIdentifier}
                className={`${styles.componentCard} ${isEarning ? styles.earningCard : styles.deductionCard} ${isInactive ? styles.componentCardInactive : ''}`}
            >
                <div className={styles.componentLeft}>
                    <span className={`${styles.categoryDot} ${isEarning ? styles.earningDot : styles.deductionDot}`} />
                    <div>
                        <p className={styles.componentName}>{component.componentName}</p>
                        <p className={styles.componentCode}>{component.componentCode}</p>
                    </div>
                </div>

                <div className={styles.componentRight}>
                    <p className={styles.valueDisplay}>{component.valueDisplay}</p>
                    {!isInactive && showActions && (
                        <div className={styles.componentActions}>
                            {!isStatutory && (
                                <>
                                    <button
                                        type="button"
                                        className={styles.componentActionBtn}
                                        onClick={() => goToEditComponent(component)}
                                        title="Edit"
                                    >
                                        <Pencil size={13} />
                                    </button>
                                </>
                            )}
                            <button
                                type="button"
                                className={`${styles.componentActionBtn} ${styles.componentActionBtnDanger}`}
                                title="Remove"
                                disabled={isRemovingId === component.componentIdentifier}
                                onClick={() => handleRemovePress(component)}
                            >
                                {isRemovingId === component.componentIdentifier
                                    ? <LoaderCircle size={13} className={styles.spin} />
                                    : <Trash2 size={13} />}
                            </button>
                            {isStatutory && (
                                <span className={styles.autoBadge}>Auto</span>
                            )}
                        </div>
                    )}
                </div>
            </article>
        )
    }

    return (
        <div className={styles.page}>

            {/* ── Header ── */}
            <header className={styles.header}>
                <button type="button" className={styles.backBtn} onClick={() => navigate(-1)}>
                    <ChevronLeft size={20} />
                </button>
                <div className={styles.headerMeta}>
                    <span className={styles.heroPill}><Sparkles size={13} /> Salary Structure</span>
                    <h1 className={styles.headerTitle}>{displayName}</h1>
                </div>
                <div className={styles.headerActions}>
                    {isRefreshing && <RefreshCw size={15} className={`${styles.spin} ${styles.refreshIcon}`} />}
                    <div className={styles.menuWrap} ref={menuRef}>
                        <button
                            type="button"
                            className={styles.menuBtn}
                            onClick={() => setMenuOpen((p) => !p)}
                        >
                            <MoreVertical size={18} />
                        </button>
                        {menuOpen && (
                            <div className={styles.dropdownMenu}>
                                <button type="button" className={styles.dropdownItem} onClick={() => goToAddComponent()}>
                                    <Plus size={14} /> Add Component
                                </button>
                                <button
                                    type="button"
                                    className={styles.dropdownItem}
                                    onClick={() => {
                                        setMenuOpen(false)
                                        navigate(`/payroll/salary-structure/${templateIdentifier}/duplicate`, {
                                            state: {
                                                templateName: displayName,
                                                activeComponents,
                                            },
                                        })
                                    }}
                                >
                                    <Copy size={14} /> Duplicate Template
                                </button>
                                <button
                                    type="button"
                                    className={`${styles.dropdownItem} ${!canRetire ? styles.dropdownItemDisabled : styles.dropdownItemDanger}`}
                                    title={!canRetire ? `${detailData?.totalStaffAssigned} staff assigned — reassign first` : ''}
                                    onClick={handleRetirePress}
                                >
                                    <Archive size={14} />
                                    Archive Template
                                    {!canRetire && <span className={styles.dropdownHint}>({detailData?.totalStaffAssigned} staff)</span>}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </header>

            {/* ── Loading ── */}
            {isLoading && (
                <section className={styles.loaderCard}>
                    <LoaderCircle size={22} className={styles.spin} />
                    <p>Loading template…</p>
                </section>
            )}

            {!isLoading && detailData && (
                <>
                    {/* ── Template info card ── */}
                    <section className={styles.infoCard}>
                        <div className={styles.infoCardTop}>
                            <div className={styles.infoNameRow}>
                                <span className={styles.infoName}>{template?.templateName}</span>
                                <span className={styles.versionBadge}>v{template?.versionNumber || 1}</span>
                                {isArchived && <span className={styles.archivedBadge}><Archive size={11} /> Archived</span>}
                            </div>
                            {template?.designationTag && (
                                <span className={styles.tagChip}><Tag size={11} /> {template.designationTag}</span>
                            )}
                        </div>
                        {template?.templateDescription && (
                            <p className={styles.infoDesc}>{template.templateDescription}</p>
                        )}
                        <p className={styles.infoMeta}>
                            Created {formatDate(template?.createdOn)}
                        </p>
                    </section>

                    {/* ── Summary bar ── */}
                    <section className={styles.summaryBar}>
                        <div className={styles.summaryItem}>
                            <span className={styles.summaryValue}>{summary?.earningsCount ?? 0}</span>
                            <span className={styles.summaryLabel}>Earnings</span>
                        </div>
                        <div className={styles.summaryDivider} />
                        <div className={styles.summaryItem}>
                            <span className={styles.summaryValue}>{summary?.deductionsCount ?? 0}</span>
                            <span className={styles.summaryLabel}>Deductions</span>
                        </div>
                        <div className={styles.summaryDivider} />
                        <div className={styles.summaryItem}>
                            <span className={`${styles.summaryValue} ${styles.summaryValueGreen}`}>
                                {summary?.estimatedMonthlyCost > 0
                                    ? `₹${Number(summary.estimatedMonthlyCost).toLocaleString('en-IN')}${summary.isEstimate ? '*' : ''}`
                                    : '—'}
                            </span>
                            <span className={styles.summaryLabel}>Est./mo</span>
                        </div>
                        <div className={styles.summaryDivider} />
                        <div className={styles.summaryItem}>
                            <span className={styles.summaryValue}>{detailData?.totalStaffAssigned ?? 0}</span>
                            <span className={styles.summaryLabel}>Staff</span>
                        </div>
                    </section>

                    {/* ── Missing BASIC warning ── */}
                    {!summary?.hasBasicComponent && (summary?.totalComponentCount ?? 0) > 0 && (
                        <section className={styles.warnBanner}>
                            <AlertTriangle size={15} />
                            <p>BASIC component missing. Add a BASIC component to make this template usable for payroll.</p>
                        </section>
                    )}

                    {/* ── Tabs ── */}
                    <div className={styles.tabRow}>
                        <button
                            type="button"
                            className={`${styles.tab} ${activeTab === 'COMPONENTS' ? styles.tabActive : ''}`}
                            onClick={() => setActiveTab('COMPONENTS')}
                        >
                            <FileText size={14} />
                            Components ({summary?.totalComponentCount ?? 0})
                        </button>
                        <button
                            type="button"
                            className={`${styles.tab} ${activeTab === 'ASSIGNED_STAFF' ? styles.tabActive : ''}`}
                            onClick={() => setActiveTab('ASSIGNED_STAFF')}
                        >
                            <Users size={14} />
                            Assigned Staff ({detailData?.totalStaffAssigned ?? 0})
                        </button>
                    </div>

                    {/* ──── COMPONENTS TAB ──── */}
                    {activeTab === 'COMPONENTS' && (
                        <section className={styles.tabContent}>

                            {activeComponents.length === 0 ? (
                                <div className={styles.emptyState}>
                                    <div className={styles.emptyIllo}>
                                        <FileText size={32} className={styles.emptyIlloIcon} />
                                    </div>
                                    <h3>No components added yet</h3>
                                    <p>Start with a BASIC component — every other calculation anchors to it.</p>

                                    <div className={styles.suggestedList}>
                                        <p className={styles.suggestedTitle}>Recommended order:</p>
                                        {SUGGESTED_COMPONENTS.map((item) => (
                                            <p key={item.code} className={styles.suggestedItem}>
                                                <span className={item.required ? styles.dotRequired : styles.dotOptional} />
                                                {item.label}{item.required ? ' (recommended)' : ''}
                                            </p>
                                        ))}
                                    </div>

                                    <button
                                        type="button"
                                        className={styles.addBasicBtn}
                                        onClick={() => goToAddComponent('BASIC')}
                                    >
                                        <Plus size={14} /> Add Basic Pay First
                                    </button>
                                </div>
                            ) : (
                                <>
                                    {/* Earnings */}
                                    {earnings.length > 0 && (
                                        <div className={styles.componentSection}>
                                            <div className={styles.sectionLabel}>
                                                <span className={styles.sectionLabelDot} style={{ background: '#16a34a' }} />
                                                EARNINGS
                                            </div>
                                            {earnings.map((c) => renderComponentCard(c))}
                                        </div>
                                    )}

                                    {/* Deductions */}
                                    {deductions.length > 0 && (
                                        <div className={styles.componentSection}>
                                            <div className={styles.sectionLabel}>
                                                <span className={styles.sectionLabelDot} style={{ background: '#dc2626' }} />
                                                DEDUCTIONS
                                            </div>
                                            {deductions.map((c) => renderComponentCard(c))}
                                        </div>
                                    )}

                                    {/* Add component CTA */}
                                    {!isArchived && (
                                        <button
                                            type="button"
                                            className={styles.addComponentBtn}
                                            onClick={() => goToAddComponent()}
                                        >
                                            <Plus size={15} /> Add Component
                                        </button>
                                    )}

                                    {/* isEstimate note */}
                                    {summary?.isEstimate && (
                                        <p className={styles.estimateNote}>
                                            * Estimated cost is partial. Some components are percentage-based and may vary per staff.
                                        </p>
                                    )}

                                    {/* Removed components */}
                                    {inactiveComponents.length > 0 && (
                                        <div className={styles.removedSection}>
                                            <button
                                                type="button"
                                                className={styles.removedToggle}
                                                onClick={() => setRemovedExpanded((p) => !p)}
                                            >
                                                <span>REMOVED ({inactiveComponents.length})</span>
                                                {removedExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                            </button>
                                            {removedExpanded && (
                                                <div className={styles.removedList}>
                                                    {inactiveComponents.map((c) => renderComponentCard(c, false))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                        </section>
                    )}

                    {/* ──── ASSIGNED STAFF TAB ──── */}
                    {activeTab === 'ASSIGNED_STAFF' && (
                        <section className={styles.tabContent}>
                            {assignedStaff.length === 0 ? (
                                <div className={styles.emptyState}>
                                    <div className={styles.emptyIllo}>
                                        <Users size={32} className={styles.emptyIlloIcon} />
                                    </div>
                                    <h3>No staff assigned yet</h3>
                                    <p>Assign this template to staff members to drive their payroll calculations.</p>
                                    <button
                                        type="button"
                                        className={styles.addBasicBtn}
                                        onClick={() => navigate(`/payroll/assign-salary-structure`, { state: { templateIdentifier, templateName: displayName } })}
                                    >
                                        Assign Staff <ChevronRight size={14} />
                                    </button>
                                </div>
                            ) : (
                                <div className={styles.staffList}>
                                    {assignedStaff.map((staff) => (
                                        <article
                                            key={staff.staffIdentifier}
                                            className={styles.staffCard}
                                            onClick={() => navigate(`/payroll/staff-compensation/${staff.staffIdentifier}`, {
                                                state: {
                                                    staffIdentifier: staff.staffIdentifier,
                                                    staffName: staff.staffName,
                                                },
                                            })}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter') {
                                                    navigate(`/payroll/staff-compensation/${staff.staffIdentifier}`, {
                                                        state: {
                                                            staffIdentifier: staff.staffIdentifier,
                                                            staffName: staff.staffName,
                                                        },
                                                    })
                                                }
                                            }}
                                        >
                                            <div className={styles.staffAvatar}>
                                                {(staff.staffName || '?')[0].toUpperCase()}
                                            </div>
                                            <div className={styles.staffInfo}>
                                                <p className={styles.staffName}>{staff.staffName}</p>
                                                {staff.designationLabel && (
                                                    <p className={styles.staffDesig}>{staff.designationLabel}</p>
                                                )}
                                                <p className={styles.staffBasic}>
                                                    Basic: ₹{Number(staff.effectiveBasic || 0).toLocaleString('en-IN')}
                                                    {staff.isBasicOverridden
                                                        ? <span className={styles.overrideBadge}> (custom)</span>
                                                        : <span className={styles.defaultBadge}> (template default)</span>}
                                                </p>
                                                <p className={styles.staffSince}>Active since {formatDate(staff.effectiveFrom)}</p>
                                            </div>
                                            <button
                                                type="button"
                                                className={styles.staffReviseBtn}
                                                onClick={(event) => {
                                                    event.stopPropagation()
                                                    navigate('/payroll/revise-salary', {
                                                        state: {
                                                            staffIdentifier: staff.staffIdentifier,
                                                            staffName: staff.staffName,
                                                            currentSalaryAssignment: {
                                                                templateIdentifier,
                                                                templateName: displayName,
                                                                basicAmount: Number(staff.effectiveBasic || 0),
                                                                effectiveFrom: staff.effectiveFrom,
                                                            },
                                                        },
                                                    })
                                                }}
                                            >
                                                Revise
                                            </button>
                                            <ChevronRight size={16} className={styles.staffChevron} />
                                        </article>
                                    ))}
                                </div>
                            )}
                        </section>
                    )}
                </>
            )}

            {confirmSheet && (
                <div className={styles.sheetBackdrop} onClick={() => setConfirmSheet(null)}>
                    <div className={styles.confirmSheet} onClick={(event) => event.stopPropagation()}>
                        <div className={styles.sheetHeaderRow}>
                            <h3 className={styles.sheetTitle}>Remove Component?</h3>
                            <button type="button" className={styles.sheetCloseBtn} onClick={() => setConfirmSheet(null)}>
                                <X size={14} />
                            </button>
                        </div>

                        <p className={styles.sheetBody}>
                            <strong>"{confirmSheet.componentName}"</strong> will be removed from this salary structure.
                            <br /><br />
                            This component will no longer appear on future payslips. Past payslips are not affected.
                        </p>

                        {confirmSheet.componentCode === 'BASIC' && (
                            <div className={styles.sheetWarningCard}>
                                <AlertTriangle size={14} />
                                <p>
                                    Removing BASIC can break percentage-based components (HRA, Special Allowance, etc.)
                                    that use BASIC as base.
                                </p>
                            </div>
                        )}

                        {confirmSheet.isStatutory && (
                            <div className={styles.sheetInfoCard}>
                                <Info size={14} />
                                <p>
                                    This is a statutory component. Removing it means this deduction will no longer apply to
                                    staff on this template.
                                </p>
                            </div>
                        )}

                        <div className={styles.sheetActions}>
                            <button type="button" className={styles.sheetCancelBtn} onClick={() => setConfirmSheet(null)}>
                                Cancel
                            </button>
                            <button
                                type="button"
                                className={styles.sheetRemoveBtn}
                                onClick={() => removeComponent(confirmSheet.componentIdentifier, confirmSheet.componentName)}
                            >
                                Remove
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showRetireSheet && (
                <div className={styles.sheetBackdrop} onClick={() => setShowRetireSheet(false)}>
                    <div className={styles.confirmSheet} onClick={(event) => event.stopPropagation()}>
                        <div className={styles.sheetHeaderRow}>
                            <h3 className={styles.sheetTitle}>Archive Template?</h3>
                            <button type="button" className={styles.sheetCloseBtn} onClick={() => setShowRetireSheet(false)}>
                                <X size={14} />
                            </button>
                        </div>

                        <p className={styles.sheetBody}>
                            <strong>"{detailData?.template?.templateName}"</strong> will be archived and removed from your active salary
                            structure list.
                            <br /><br />
                            All past payslips generated using this template remain accurate and unaffected.
                        </p>

                        <div className={styles.sheetInfoCard}>
                            <Info size={14} />
                            <p className={styles.sheetInfoList}>
                                • Future payroll runs cannot use this template{`\n`}
                                • Existing staff assignments must be moved first{`\n`}
                                • You can view archived templates from the salary structure list
                            </p>
                        </div>

                        <div className={styles.sheetActions}>
                            <button type="button" className={styles.sheetCancelBtn} onClick={() => setShowRetireSheet(false)}>
                                Cancel
                            </button>
                            <button
                                type="button"
                                className={`${styles.sheetArchiveBtn} ${isRetiring ? styles.sheetArchiveBtnDisabled : ''}`}
                                disabled={isRetiring}
                                onClick={retireTemplate}
                            >
                                {isRetiring ? (
                                    <><LoaderCircle size={14} className={styles.spin} /> Archiving...</>
                                ) : (
                                    <>Archive Template</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {blockedStaffData && (
                <div className={styles.sheetBackdrop} onClick={() => setBlockedStaffData(null)}>
                    <div className={styles.confirmSheet} onClick={(event) => event.stopPropagation()}>
                        <div className={styles.sheetHeaderRow}>
                            <h3 className={styles.sheetTitle}>Staff Reassignment Required</h3>
                            <button type="button" className={styles.sheetCloseBtn} onClick={() => setBlockedStaffData(null)}>
                                <X size={14} />
                            </button>
                        </div>

                        <p className={styles.blockedSubtitle}>
                            {blockedStaffData?.blockedStaffCount || 0} staff member(s) must be assigned to a different salary structure before
                            "{blockedStaffData?.templateName || displayName}" can be archived.
                        </p>

                        <div className={styles.blockedStaffList}>
                            {(blockedStaffData?.blockedStaff || []).map((staff) => (
                                <div key={staff.staffIdentifier} className={styles.blockedStaffRow}>
                                    <div className={styles.blockedStaffMeta}>
                                        <p className={styles.blockedStaffName}>{staff.staffName}</p>
                                        <p className={styles.blockedStaffDesignation}>{staff.designationLabel || '—'}</p>
                                        <p className={styles.blockedStaffBasic}>Basic: ₹{Number(staff.basicAmount || 0).toLocaleString('en-IN')}</p>
                                    </div>
                                    <button
                                        type="button"
                                        className={styles.reassignBtn}
                                        onClick={() => {
                                            setBlockedStaffData(null)
                                            navigate('/payroll/assign-salary-structure', {
                                                state: {
                                                    staffIdentifier: staff.staffIdentifier,
                                                    staffName: staff.staffName,
                                                    excludeTemplateIdentifier: templateIdentifier,
                                                },
                                            })
                                        }}
                                    >
                                        Reassign <ChevronRight size={13} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default SalaryStructureDetailScreen